/**
 * Subscription Plans Component
 *
 * Displays monthly and annual subscription options with features.
 * Handles checkout flow integration with Stripe.
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModals } from './auth/AuthModalManager';
import stripeService from '../services/stripeService';
import MaterialIcon from './MaterialIcon';
import MainHeader from './MainHeader';
import './SubscriptionPlans.css';

const SubscriptionPlans = () => {
  const { user, isAuthenticated, isSubscriber } = useAuth();
  const { openLogin } = useAuthModals();
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Plan configurations
  const plans = {
    monthly: {
      name: 'Monthly',
      price: 15,
      priceId: process.env.REACT_APP_STRIPE_PRICE_ID_MONTHLY || 'price_monthly_placeholder',
      interval: 'month',
      description: 'Perfect for trying it out',
      savings: null,
    },
    annual: {
      name: 'Annual',
      price: 150,
      priceId: process.env.REACT_APP_STRIPE_PRICE_ID_ANNUAL || 'price_annual_placeholder',
      interval: 'year',
      description: 'Best value - Save $30/year',
      savings: 30,
    },
  };

  const features = [
    {
      icon: 'local_shipping',
      title: 'Monthly Exclusive Sticker',
      description: 'Get a unique, limited-edition sticker delivered to your door every month',
    },
    {
      icon: 'how_to_vote',
      title: 'Vote on Next Design',
      description: 'Help choose what sticker we create next month - your voice matters!',
    },
    {
      icon: 'emoji_events',
      title: 'Monthly Scene Contest',
      description: 'Submit one scene per month - winner gets their scene printed for FREE',
    },
    {
      icon: 'verified',
      title: 'Exclusive Designs',
      description: 'Access subscriber-only stickers not available to the public',
    },
    {
      icon: 'bolt',
      title: 'Early Access',
      description: 'Get first look at new designs 30 days before public release',
    },
  ];

  const handleSubscribe = async () => {
    setError('');

    // Require authentication
    if (!isAuthenticated) {
      openLogin();
      return;
    }

    // Already subscribed
    if (isSubscriber) {
      setError('You already have an active subscription!');
      return;
    }

    setLoading(true);

    try {
      const plan = plans[selectedPlan];

      // Get auth token from localStorage
      const token = localStorage.getItem('ce_auth_token');

      if (!token) {
        throw new Error('Authentication required');
      }

      // Create checkout session and redirect to Stripe
      const result = await stripeService.createSubscriptionCheckout(plan.priceId, token);

      if (!result.success) {
        throw new Error(result.error || 'Failed to create checkout session');
      }

      // User will be redirected to Stripe Checkout
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      <MainHeader currentPage="subscription" />
      <div className="subscription-plans">
        {/* Hero Section */}
        <div className="subscription-hero">
          <h1>Join the Sticker of the Month Club</h1>
          <p className="subscription-subtitle">
            Get exclusive stickers, vote on designs, and win free custom scenes every month
          </p>
        </div>

        {/* Already Subscribed Banner */}
        {isSubscriber && (
          <div className="subscription-active-banner">
            <MaterialIcon name="verified" />
            <div>
              <strong>You're a Subscriber!</strong>
              <p>Thank you for your support. Manage your subscription in your account settings.</p>
            </div>
          </div>
        )}

        {/* Plan Selection */}
        <div className="subscription-plan-selector">
          <h2>Choose Your Plan</h2>

          <div className="subscription-plan-toggle">
            <button
              className={`plan-toggle-button ${selectedPlan === 'monthly' ? 'active' : ''}`}
              onClick={() => setSelectedPlan('monthly')}
            >
              <span className="plan-toggle-name">Monthly</span>
              <span className="plan-toggle-price">$15/mo</span>
            </button>

            <button
              className={`plan-toggle-button ${selectedPlan === 'annual' ? 'active' : ''}`}
              onClick={() => setSelectedPlan('annual')}
            >
              <span className="plan-toggle-name">Annual</span>
              <span className="plan-toggle-price">$150/yr</span>
              <span className="plan-toggle-savings">Save $30</span>
            </button>
          </div>

          {/* Selected Plan Details */}
          <div className="subscription-plan-details">
            <div className="plan-details-header">
              <h3>{plans[selectedPlan].name} Plan</h3>
              <div className="plan-details-price">
                <span className="price-amount">${plans[selectedPlan].price}</span>
                <span className="price-interval">/{plans[selectedPlan].interval}</span>
              </div>
            </div>

            {plans[selectedPlan].savings && (
              <div className="plan-details-savings">
                💰 Save ${plans[selectedPlan].savings} per year
              </div>
            )}

            <p className="plan-details-description">{plans[selectedPlan].description}</p>

            {/* Error Message */}
            {error && (
              <div className="subscription-error">
                <MaterialIcon name="error" />
                <span>{error}</span>
              </div>
            )}

            {/* Subscribe Button */}
            <button
              className="subscription-subscribe-button"
              onClick={handleSubscribe}
              disabled={loading || isSubscriber}
            >
              {loading ? (
                <>
                  <span className="button-spinner" />
                  Processing...
                </>
              ) : isSubscriber ? (
                <>
                  <MaterialIcon name="check_circle" />
                  Already Subscribed
                </>
              ) : (
                <>
                  <MaterialIcon name="card_membership" />
                  Subscribe Now
                </>
              )}
            </button>

            <p className="subscription-trial-info">Cancel anytime • No long-term commitment</p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="subscription-features">
          <h2>What You Get Every Month</h2>

          <div className="subscription-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="subscription-feature-card">
                <div className="feature-card-icon">
                  <MaterialIcon name={feature.icon} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How It Works */}
        <div className="subscription-how-it-works">
          <h2>How It Works</h2>

          <div className="subscription-steps">
            <div className="subscription-step">
              <div className="step-number">1</div>
              <h3>Subscribe</h3>
              <p>Choose your plan and complete checkout securely with Stripe</p>
            </div>

            <div className="subscription-step-arrow">
              <MaterialIcon name="arrow_forward" />
            </div>

            <div className="subscription-step">
              <div className="step-number">2</div>
              <h3>Vote & Create</h3>
              <p>Help choose next month's design and submit your scene for the contest</p>
            </div>

            <div className="subscription-step-arrow">
              <MaterialIcon name="arrow_forward" />
            </div>

            <div className="subscription-step">
              <div className="step-number">3</div>
              <h3>Receive Sticker</h3>
              <p>Get your exclusive sticker delivered to your mailbox each month</p>
            </div>

            <div className="subscription-step-arrow">
              <MaterialIcon name="arrow_forward" />
            </div>

            <div className="subscription-step">
              <div className="step-number">4</div>
              <h3>Repeat & Enjoy</h3>
              <p>New sticker, new vote, new contest every single month!</p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="subscription-faq">
          <h2>Frequently Asked Questions</h2>

          <div className="subscription-faq-grid">
            <div className="subscription-faq-item">
              <h3>Can I cancel anytime?</h3>
              <p>
                Yes! Cancel anytime from your account settings. You'll continue to receive benefits
                until the end of your current billing period.
              </p>
            </div>

            <div className="subscription-faq-item">
              <h3>What if I miss a month?</h3>
              <p>
                Don't worry! Your subscription continues, and you'll get the next month's sticker.
                We can't send past months retroactively.
              </p>
            </div>

            <div className="subscription-faq-item">
              <h3>How does voting work?</h3>
              <p>
                Each month we present 3-5 design concepts. You vote for your favorite, and the
                winning design becomes next month's exclusive sticker!
              </p>
            </div>

            <div className="subscription-faq-item">
              <h3>What about shipping?</h3>
              <p>
                Shipping is included in your subscription! We ship to US and Canada. International
                shipping available for additional fee.
              </p>
            </div>

            <div className="subscription-faq-item">
              <h3>How is the contest winner chosen?</h3>
              <p>
                We randomly select one winning scene each month from all subscriber submissions.
                Winner gets their scene printed for FREE!
              </p>
            </div>

            <div className="subscription-faq-item">
              <h3>Can I buy individual stickers?</h3>
              <p>
                Subscriber-exclusive designs are only available to active subscribers. But you can
                still buy all our regular stickers anytime!
              </p>
            </div>
          </div>
        </div>

        {/* CTA Footer */}
        <div className="subscription-cta-footer">
          <h2>Ready to Join?</h2>
          <p>Start getting exclusive stickers, voting on designs, and competing for free scenes!</p>
          <button
            className="subscription-subscribe-button subscription-subscribe-button-large"
            onClick={handleSubscribe}
            disabled={loading || isSubscriber}
          >
            {isSubscriber ? (
              <>
                <MaterialIcon name="check_circle" />
                You're Already a Subscriber!
              </>
            ) : (
              <>
                <MaterialIcon name="card_membership" />
                Subscribe Now - ${plans[selectedPlan].price}/{plans[selectedPlan].interval}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default SubscriptionPlans;
